import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Logo } from "./brand";
import { brand } from "./content";

/** Shared page container: every page (landing, docs, app) uses this width and gutter. */
export const SITE_WRAP = "mx-auto w-full max-w-[1400px] px-4 sm:px-10";

export function Container({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`${SITE_WRAP} ${className}`}>{children}</div>;
}

type HeaderLink =
  | { label: string; href: string; to?: never }
  | { label: string; to: "/" | "/docs" | "/app" | "/app/new"; href?: never };

/** The one navigation, on every page. */
export const SITE_LINKS: HeaderLink[] = [
  { label: "How it works", href: "/#how" },
  { label: "Docs", to: "/docs" },
  { label: "App", to: "/app" },
];

/** Inside the app the landing's "How it works" anchor is left out. */
export const APP_LINKS = SITE_LINKS.filter((l) => l.href !== "/#how");

function HeaderNavLink({
  link,
  className,
  onClick,
}: {
  link: HeaderLink;
  className: string;
  onClick?: () => void;
}) {
  return link.to ? (
    <Link
      to={link.to}
      onClick={onClick}
      className={className}
      activeOptions={{ exact: link.to === "/" }}
      activeProps={{ "aria-current": "page" }}
    >
      {link.label}
    </Link>
  ) : (
    <a href={link.href} onClick={onClick} className={className}>
      {link.label}
    </a>
  );
}

/**
 * The wallet control outside the app: a link that opens the app's connect dialog, so the landing and docs
 * never load the wallet stack. The app passes the real ConnectButton to the header instead.
 */
export function WalletLink({ className = "ds-btn ds-btn-sm ds-btn-primary" }: { className?: string }) {
  // Someone who connected before goes straight back to their vaults; a newcomer starts building one.
  const [returning] = useState(() => {
    try {
      return !!localStorage.getItem("wagmi.recentConnectorId");
    } catch {
      return false;
    }
  });
  return (
    <Link
      to={returning ? "/app" : "/app/new"}
      search={returning ? {} : { connect: true }}
      className={className}
    >
      {returning ? (
        "Open app"
      ) : (
        <>
          <span className="max-[359px]:hidden">Connect wallet</span>
          <span className="min-[360px]:hidden">Connect</span>
        </>
      )}
    </Link>
  );
}

/**
 * The one site header outside the landing (docs, app, 404), drawn like the landing's: logo, links beside it,
 * the wallet control at the end. The app passes the real ConnectButton through `wallet`.
 */
export function SiteHeader({ wallet, links = SITE_LINKS }: { wallet?: ReactNode; links?: HeaderLink[] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <header className="ds-header fixed inset-x-0 top-0 z-50">
      <div className={`${SITE_WRAP} flex h-[72px] items-center gap-8`}>
        <Link to="/" className="flex shrink-0 items-center" aria-label="bips home">
          <Logo height={24} />
        </Link>
        <nav aria-label="Primary" className="ml-3 hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <HeaderNavLink key={l.label} link={l} className="ds-navlink" />
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {wallet ?? <WalletLink />}
          <button
            type="button"
            className="grid size-11 place-items-center md:hidden"
            aria-expanded={open}
            aria-controls="ds-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={26} strokeWidth={1.6} /> : <Menu size={26} strokeWidth={1.6} />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          id="ds-menu"
          aria-label="Mobile"
          className="border-t border-black/10 bg-(--vx-canvas) px-4 pt-4 pb-8 md:hidden"
        >
          <ul className="grid gap-1">
            {links.map((l) => (
              <li key={l.label}>
                <HeaderNavLink
                  link={l}
                  onClick={() => setOpen(false)}
                  className="block py-3 font-serif text-[30px] tracking-[-0.03em]"
                />
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}

/** Short risk note for the footer; the full disclosures live in the docs. */
const FOOTER_NOTE =
  "Beta. Contracts are unaudited. Tokenized stocks carry issuer and eligibility risk and aren't available to US persons. Not investment advice. Not affiliated with Uniswap Labs or any issuer.";

/** The one site footer outside the landing, drawn like the landing's. */
export function SiteFooter() {
  const links = [
    { label: "Build a vault", to: "/app/new" as const },
    { label: "Open the app", to: "/app" as const },
    { label: "Docs", to: "/docs" as const },
  ];
  return (
    <footer className="mt-auto">
      <div className={`${SITE_WRAP} pt-[90px] pb-[110px]`}>
        <div className="flex flex-wrap justify-between gap-10">
          <div className="grid gap-3.5">
            <Link to="/" aria-label="bips home" className="inline-flex text-black">
              <Logo height={26} />
            </Link>
            <p className="font-serif text-[22px] leading-[1.2] text-(--vx-ink-2) italic">{brand.tagline}</p>
          </div>
          <nav
            aria-label="Footer"
            className="grid grid-cols-[repeat(2,auto)] content-start gap-x-12 gap-y-3.5"
          >
            {links.map((l) => (
              <Link key={l.label} to={l.to} className="ds-flink">
                {l.label}
              </Link>
            ))}
            <a
              href={brand.x.href}
              target="_blank"
              rel="noreferrer"
              className="ds-flink inline-flex items-center gap-1"
            >
              {brand.x.handle} <ArrowUpRight size={14} aria-hidden="true" />
            </a>
          </nav>
        </div>
        <p className="mt-14 max-w-[880px] text-[13px] leading-[1.6] text-(--vx-ink-3)">{FOOTER_NOTE}</p>
      </div>
    </footer>
  );
}
